import React from 'react'
import dayjs from 'dayjs'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faMessage,
  faCancel,
  faStethoscope, // Changed from faEye
  faUserCheck,
  faUserClock,
  faVideo,
  faVideoCamera,
  faPhone,
  faUser
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { Space, Button, Tooltip, Select } from 'antd'
import {
  handleCancelBooking as cancelBookingUtil,
  handleWasNoShow,
  generateStatus
} from '../../../../../lib/booking-utils' // Aliased to avoid conflict
import CoreEnums from '../../../../../../core/utils/enums'
import templates from '../../../../../Admin/bookings/utils/templates'

const TableColumns = ({
  token,
  userRef,
  currentUserData,
  isBilling,
  billingState,
  employeeBookingsState,
  filteredValues,
  handleReassignment,
  rangeFilter,
  handleSetPaymentMethod,
  services,
  setTeamChatProps,
  handleOpenBooking,
  readOnly,
  updateBooking, // Passed from parent
  handleGetAllBookings, // Passed from parent
  authState, // Passed from parent
  handleClaimAppointment, // Passed from parent
  statuses,
  handleChangeStatus,
  changingStatus
}) => {
  // Helper function to check if appointment is overdue for no-show marking
  const isAppointmentOverdue = (record) => {
    // Early checks
    if (
      record.wasNoShow ||
      record.status === CoreEnums.bookingStatuses.completed ||
      record.status === CoreEnums.bookingStatuses.cancelled ||
      record.status !== CoreEnums.bookingStatuses.checkin
    ) {
      return { eligible: false, reason: 'Not eligible for no-show marking' }
    }

    // Only for reception and medical professionals
    if (
      authState.agiliteUser?.extraData.role.type !== 'reception' &&
      authState.agiliteUser?.extraData.role.type !== 'medical_professional'
    ) {
      return { eligible: false, reason: 'Not authorized' }
    }

    // Check if appointment is 3+ hours overdue
    if (record.bookingDate && record.startTime) {
      const appointmentDate = dayjs(record.bookingDate)
      const [hours, minutes] = record.startTime.split(':')
      const appointmentDateTime = appointmentDate.hour(parseInt(hours)).minute(parseInt(minutes)).second(0)

      if (!appointmentDateTime.isValid()) {
        return { eligible: false, reason: 'Invalid appointment time' }
      }

      const currentTime = dayjs()
      const hoursOverdue = currentTime.diff(appointmentDateTime, 'hours', true)

      if (hoursOverdue >= 3) {
        return {
          eligible: true,
          hoursOverdue: hoursOverdue.toFixed(1),
          reason: `Mark as no-show - appointment ${hoursOverdue.toFixed(1)} hours overdue`
        }
      } else {
        const hoursUntilEligible = 3 - hoursOverdue
        return {
          eligible: false,
          reason: `Too early - wait ${hoursUntilEligible.toFixed(1)} more hours`
        }
      }
    }

    return { eligible: false, reason: 'Missing appointment details' }
  }
  const actionsColumn = {
    title: <h2>Actions</h2>,
    key: 'actions',
    align: 'center',
    fixed: 'right',
    width: 200,
    render: (_, record) => (
      <Space size={8}>
        <Tooltip title='Start Consultation'>
          <Button
            type='primary'
            size='small'
            onClick={(e) => {
              e.stopPropagation()
              handleOpenBooking(record)
            }}
            icon={<FontAwesomeIcon icon={faStethoscope} />}
            style={{
              height: '32px',
              width: '32px',
              minWidth: '32px',
              background: token.actionColor,
              borderRadius: '50%',
              boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0
            }}
            disabled={readOnly}
          />
        </Tooltip>
        <Tooltip title='Team Chat'>
          <Button
            type='primary'
            size='small'
            onClick={(e) => {
              e.stopPropagation()
              setTeamChatProps({ userRef: record.userRef, isOpen: true })
            }}
            icon={<FontAwesomeIcon icon={faMessage} />}
            style={{
              height: '32px',
              width: '32px',
              minWidth: '32px',
              background: token.actionColor,
              borderRadius: '50%',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0
            }}
          />
        </Tooltip>
        {!readOnly && !isBilling && record.status !== CoreEnums.bookingStatuses.cancelled && (
          <Tooltip title='Cancel appointment'>
            <Button
              type='primary'
              size='small'
              onClick={(e) => {
                e.stopPropagation()
                cancelBookingUtil(authState, record, updateBooking, handleGetAllBookings, token)
              }}
              icon={<FontAwesomeIcon icon={faCancel} />}
              style={{
                height: '32px',
                width: '32px',
                minWidth: '32px',
                background: '#ff4d4f',
                borderRadius: '50%',
                boxShadow: '0 2px 4px rgba(255, 77, 79, 0.2)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0
              }}
            />
          </Tooltip>
        )}
        {!record.medicalProfRef && (
          <Tooltip title='Claim appointment'>
            <Button
              type='primary'
              size='small'
              onClick={() => {
                handleClaimAppointment(record)
              }}
              icon={<FontAwesomeIcon icon={faUserCheck} />}
              style={{
                height: '32px',
                width: '32px',
                minWidth: '32px',
                background: token.colorSuccess,
                borderRadius: '50%',
                boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0
              }}
            />
          </Tooltip>
        )}
        {(() => {
          const overdueCheck = isAppointmentOverdue(record)
          if (overdueCheck.eligible) {
            return (
              <Tooltip title={overdueCheck.reason}>
                <Button
                  type='primary'
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation()
                    handleWasNoShow(record, updateBooking, handleGetAllBookings, token)
                  }}
                  icon={<FontAwesomeIcon icon={faUserClock} />}
                  style={{
                    height: '32px',
                    width: '32px',
                    minWidth: '32px',
                    background: token.colorWarning,
                    borderRadius: '50%',
                    boxShadow: '0 2px 4px rgba(255, 193, 7, 0.2)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                  }}
                  disabled={readOnly}
                />
              </Tooltip>
            )
          } else if (
            !overdueCheck.eligible &&
            overdueCheck.reason !== 'Not eligible for no-show marking' &&
            overdueCheck.reason !== 'Not authorized'
          ) {
            return (
              <Tooltip title={overdueCheck.reason}>
                <Button
                  type='primary'
                  size='small'
                  icon={<FontAwesomeIcon icon={faUserClock} />}
                  style={{
                    height: '32px',
                    width: '32px',
                    minWidth: '32px',
                    background: token.colorTextDisabled,
                    borderRadius: '50%',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    opacity: 0.5,
                    cursor: 'not-allowed'
                  }}
                  disabled={true}
                />
              </Tooltip>
            )
          }
          return null
        })()}
      </Space>
    )
  }

  const dateTimeColumn = {
    title: <h2>Date & Time</h2>,
    key: 'dateTime',
    width: 150,
    render: (_, record) => (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontWeight: 500, color: token.colorTextLabel }}>
          {record.bookingDate ? dayjs(record.bookingDate).format('DD MMM YYYY') : 'Non-Scheduled'}
        </span>
        <span style={{ color: token.colorTextDescription }}>{record.startTime}</span>
      </div>
    ),
    sorter: (a, b) => {
      const dateA = dayjs(a.bookingDate).format('YYYY-MM-DD')
      const dateB = dayjs(b.bookingDate).format('YYYY-MM-DD')
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB)
      }
      return a.startTime.localeCompare(b.startTime)
    }
  }

  const callTypeColumn = {
    title: 'Method',
    key: 'callType',
    width: 90,
    render: (_, record) => {
      let iconProps = { style: { fontSize: '22px' } }
      let tooltipText = 'Unknown Method'

      if (record.callType === 'video') {
        iconProps.icon = faVideoCamera
        iconProps.style.color = token.colorPrimary
        tooltipText = 'In App Video'
      } else if (record.callType === 'phone') {
        iconProps.icon = faPhone
        iconProps.style.color = token.colorWarning
        tooltipText = 'Phone Call'
      } else if (record.callType === 'whatsapp') {
        iconProps.icon = faWhatsapp
        iconProps.style.color = token.colorSuccess
        tooltipText = 'WhatsApp Video'
      } else {
        iconProps.icon = faUser
        iconProps.style.color = token.colorTextLabel
        tooltipText = 'In Person'
      }

      return (
        <center>
          <Tooltip title={tooltipText}>
            <FontAwesomeIcon {...iconProps} />
          </Tooltip>
        </center>
      )
    }
  }

  const baseColumns = templates
    .columnTemplate(
      token,
      userRef ? currentUserData : isBilling ? billingState : employeeBookingsState,
      false,
      (record) => cancelBookingUtil(authState, record, updateBooking, handleGetAllBookings, token),
      authState,
      filteredValues,
      handleReassignment,
      rangeFilter,
      handleSetPaymentMethod,
      services,
      setTeamChatProps,
      handleOpenBooking,
      isBilling
    )
    .filter((record) => record.hidden !== true)

  // Custom status column with dropdown
  const statusColumn = {
    title: <h2>Status</h2>,
    dataIndex: 'status',
    key: 'status',
    width: 180,
    render: (text, record) => {
      // If user is read-only or doesn't have permission, just show the status
      if (readOnly || isBilling) {
        return generateStatus(record, record.status, token, record.wasNoShow)
      }

      // Function to get color for status
      const getStatusColor = (statusValue) => {
        switch (statusValue) {
          case 'procedure':
            return token.colorSecondary
          case 'diagnosis':
            return token.colorWarning
          case 'new_booking':
          case 'check_in':
            return token.colorPrimary
          case CoreEnums.bookingStatuses.checkin:
            return token.colorPrimaryText
          case CoreEnums.bookingStatuses.screening:
            return token.colorSecondary
          case CoreEnums.bookingStatuses.consultation:
            return token.colorWarning
          case CoreEnums.bookingStatuses.reception:
            return token.colorInfo
          case CoreEnums.bookingStatuses.completed:
            return token.colorSuccess
          case CoreEnums.bookingStatuses.cancelled:
            return token.colorError
          default:
            return token.colorTextTertiary // Changed from undefined to avoid white
        }
      }

      // Otherwise show the dropdown
      return (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: getStatusColor(record.status),
            minWidth: '160px'
          }}
        >
          <Select
            style={{
              width: '100%',
              backgroundColor: 'transparent'
            }}
            size='small'
            value={record.status}
            placeholder='Select Status'
            options={statuses.map((status) => ({
              ...status,
              label: status.label
            }))}
            onChange={(value, option) => {
              handleChangeStatus(value, option.key, record)
            }}
            loading={changingStatus[record._id]}
            disabled={
              changingStatus[record._id] || record.status === CoreEnums.bookingStatuses.cancelled || record.wasNoShow
            }
            optionRender={(option) => (
              <span
                style={{
                  display: 'block',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: getStatusColor(option.value),
                  color: '#fff',
                  width: '100%'
                }}
              >
                {option.label}
              </span>
            )}
            dropdownStyle={{ minWidth: '180px' }}
            suffixIcon={<span style={{ color: '#fff' }}>▼</span>}
            className='status-select-colored'
          />
        </div>
      )
    }
  }

  const filteredColumns = baseColumns.filter(
    (col) => !(col.dataIndex === 'bookingDate' || col.dataIndex === 'startTime' || col.dataIndex === 'status')
  )

  if (readOnly) {
    return [
      filteredColumns[0],
      dateTimeColumn,
      callTypeColumn,
      ...filteredColumns.slice(1),
      statusColumn,
      {
        ...actionsColumn,
        render: () => (
          <Space size={8}>
            <Button
              type='primary'
              size='small'
              icon={<FontAwesomeIcon icon={faStethoscope} />}
              style={{
                height: '32px',
                width: '32px',
                minWidth: '32px',
                background: `${token.actionColor}30`,
                borderRadius: '50%',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                cursor: 'not-allowed'
              }}
              disabled
            />
            <Button
              type='primary'
              size='small'
              icon={<FontAwesomeIcon icon={faMessage} />}
              style={{
                height: '32px',
                width: '32px',
                minWidth: '32px',
                background: `${token.actionColor}30`,
                borderRadius: '50%',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                cursor: 'not-allowed'
              }}
              disabled
            />
          </Space>
        )
      }
    ]
  }

  return [filteredColumns[0], dateTimeColumn, ...filteredColumns.slice(1), callTypeColumn, statusColumn, actionsColumn]
}

export default TableColumns
